import { IDatePickerStrings } from '@fluentui/react';
import L from "leaflet";
import { IAssignmentDTO, ICreateOrUpdateSupplierDTO, ILayer } from '../models';
import { orderBy } from 'lodash';
import memoizeOne from 'memoize-one';
import translationDA from '../locales/da.json';
import translationDE from '../locales/de.json';

export default class Utilities {
    private static translations = {
        da: translationDA,
        de: translationDE
    } as const;

    private static getCurrentLanguage(): 'da' | 'de' {
        const savedLang = localStorage.getItem('appLanguage');
        return savedLang === 'de' ? 'de' : 'da';
    }

    public static getTranslation(key: string, fallback: string): string {
        const lang = Utilities.getCurrentLanguage();
        const source = Utilities.translations[lang] as any;
        const keys = key.split('.');
        let result = source;

        for (const k of keys) {
            if (result && typeof result === 'object' && k in result) {
                result = result[k];
            } else {
                return fallback;
            }
        }

        return typeof result === 'string' ? result : fallback;
    }

    public static get DayPickerStrings(): IDatePickerStrings {
        return {
            months: [
                Utilities.getTranslation('dayPicker.months.january', 'Januar'),
                Utilities.getTranslation('dayPicker.months.february', 'Februar'),
                Utilities.getTranslation('dayPicker.months.march', 'Marts'),
                Utilities.getTranslation('dayPicker.months.april', 'April'),
                Utilities.getTranslation('dayPicker.months.may', 'Maj'),
                Utilities.getTranslation('dayPicker.months.june', 'Juni'),
                Utilities.getTranslation('dayPicker.months.july', 'Juli'),
                Utilities.getTranslation('dayPicker.months.august', 'August'),
                Utilities.getTranslation('dayPicker.months.september', 'September'),
                Utilities.getTranslation('dayPicker.months.october', 'Oktober'),
                Utilities.getTranslation('dayPicker.months.november', 'November'),
                Utilities.getTranslation('dayPicker.months.december', 'December')
            ],
            shortMonths: [
                Utilities.getTranslation('dayPicker.shortMonths.january', 'Jan'),
                Utilities.getTranslation('dayPicker.shortMonths.february', 'Feb'),
                Utilities.getTranslation('dayPicker.shortMonths.march', 'Mar'),
                Utilities.getTranslation('dayPicker.shortMonths.april', 'Apr'),
                Utilities.getTranslation('dayPicker.shortMonths.may', 'Maj'),
                Utilities.getTranslation('dayPicker.shortMonths.june', 'Jun'),
                Utilities.getTranslation('dayPicker.shortMonths.july', 'Jul'),
                Utilities.getTranslation('dayPicker.shortMonths.august', 'Aug'),
                Utilities.getTranslation('dayPicker.shortMonths.september', 'Sep'),
                Utilities.getTranslation('dayPicker.shortMonths.october', 'Okt'),
                Utilities.getTranslation('dayPicker.shortMonths.november', 'Nov'),
                Utilities.getTranslation('dayPicker.shortMonths.december', 'Dec')
            ],
            days: [
                Utilities.getTranslation('dayPicker.days.sunday', 'Søndag'),
                Utilities.getTranslation('dayPicker.days.monday', 'Mandag'),
                Utilities.getTranslation('dayPicker.days.tuesday', 'Tirsdag'),
                Utilities.getTranslation('dayPicker.days.wednesday', 'Onsdag'),
                Utilities.getTranslation('dayPicker.days.thursday', 'Torsdag'),
                Utilities.getTranslation('dayPicker.days.friday', 'Fredag'),
                Utilities.getTranslation('dayPicker.days.saturday', 'Lørdag')
            ],
            shortDays: [
                Utilities.getTranslation('dayPicker.shortDays.sunday', 'S'),
                Utilities.getTranslation('dayPicker.shortDays.monday', 'M'),
                Utilities.getTranslation('dayPicker.shortDays.tuesday', 'TI'),
                Utilities.getTranslation('dayPicker.shortDays.wednesday', 'O'),
                Utilities.getTranslation('dayPicker.shortDays.thursday', 'TO'),
                Utilities.getTranslation('dayPicker.shortDays.friday', 'F'),
                Utilities.getTranslation('dayPicker.shortDays.saturday', 'L')
            ],
            goToToday: Utilities.getTranslation('dayPicker.goToToday', 'Gå til dags dato'),
            prevMonthAriaLabel: Utilities.getTranslation('dayPicker.prevMonthAriaLabel', 'Gå til forrige måned'),
            nextMonthAriaLabel: Utilities.getTranslation('dayPicker.nextMonthAriaLabel', 'Gå til næste måned'),
            prevYearAriaLabel: Utilities.getTranslation('dayPicker.prevYearAriaLabel', 'Gå til forrige år'),
            nextYearAriaLabel: Utilities.getTranslation('dayPicker.nextYearAriaLabel', 'Gå til næste år'),
            closeButtonAriaLabel: Utilities.getTranslation('dayPicker.closeButtonAriaLabel', 'Luk datovælger')
        };
    }

    public static FormatDate(date?: Date): string {
        return !date ? '' : Utilities.pad(date.getDate(), 2) + '-' + Utilities.pad((date.getMonth() + 1), 2) + '-' + date.getFullYear();
    };

    // From UTC to local time and supporting Safari on iOS
    public static ConvertFromUTCToLocalTime(dateAsString: string)
    {
        let parts : string[] = dateAsString.split(/[^0-9]/);
        return new Date(Date.UTC(
            parseInt(parts[0]),
            parseInt(parts[1]) - 1,
            parseInt(parts[2]),
            parseInt(parts[3]),
            parseInt(parts[4]),
            parseInt(parts[5])));
    }

    private static pad(num : number, size : number) {
        let s = num + "";
        while (s.length < size) s = "0" + s;
        return s;
    }
    

    public static CopyAndSort<T>(items: T[], columnKey: string, isSortedDescending?: boolean): T[] {
        const key = columnKey as keyof T;

        if(key == "created")
        {
            return items.slice(0).sort((a: T, b: T) => 
            {
                let field = key as string;
                if((a as IAssignmentDTO).status == Status.Approved)
                {
                    field = "approvedDate";
                }

                return ((isSortedDescending ? new Date(a[field] as any).getTime() < new Date(b[field] as any).getTime() : new Date(a[field] as any).getTime() > new Date(b[field] as any).getTime()) ? 1 : -1)
            });
        } else if(key == "estimatedAmount")
        {
            return orderBy(items, [a => a[key] ?? 0], [isSortedDescending ? "desc" : "asc"]);
        } else
        {            
            return orderBy(items, [a => (a[key] + "").toLocaleLowerCase()], [isSortedDescending ? "desc" : "asc"]);
        }
    }

    public static CheckNotNullOrEmpty(text: string | undefined | null)
    {
        if(text == undefined || text == null)
        {
            return false;
        }

        return text != "" && text + "" != "";
    }

    public static NewGuid() 
    {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          var r = Math.random() * 16 | 0,
            v = c == 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
    }
}

//Allow custom projection
(window.L as any).Proj = require('proj4leaflet');

export class MapUtilities
{
    public static CreateTextIcon(html: string)
    {
        return L.divIcon({ className: 'text-labels', html: html });
    }
    
    public static none = L.icon({ iconUrl: "images/marker-icon-transparent.png" });

    public static CreateFixedTaskPositionIcon = memoizeOne((zoomLevel: number) : L.Icon => {
      let defaultWidth = 25;
      let defaultHeight = 41;

      let sizes = {
        13: [defaultWidth, defaultHeight], //nearest zoom level, e.g. biggest icon size
        12: [defaultWidth * 0.95, defaultHeight * 0.95],
        11: [defaultWidth * 0.90, defaultHeight * 0.90],
        10: [defaultWidth * 0.85, defaultHeight * 0.85],
        9:  [defaultWidth * 0.80, defaultHeight * 0.80],
        8:  [defaultWidth * 0.50, defaultHeight * 0.50],
        7:  [defaultWidth * 0.45, defaultHeight * 0.45],
        6:  [defaultWidth * 0.40, defaultHeight * 0.40],
        5:  [defaultWidth * 0.35, defaultHeight * 0.35],
        4:  [defaultWidth * 0.30, defaultHeight * 0.30],
        3:  [defaultWidth * 0.25, defaultHeight * 0.25]
      };

      return L.icon({ iconUrl: "images/marker-icon-gold.png", iconSize: sizes[zoomLevel] });
    });

    public static CreateStackIcon = memoizeOne((zoomLevel, direction: string = "top", tooltipAnchor = [0, -15] as L.PointExpression) : L.Icon =>
    {
      let defaultWidth = 34;
      let defaultHeight = 30;

      let sizes = {
        13: [defaultWidth, defaultHeight], //nearest zoom level, e.g. biggest icon size
        12: [defaultWidth * 0.95, defaultHeight * 0.95],
        11: [defaultWidth * 0.90, defaultHeight * 0.90],
        10: [defaultWidth * 0.85, defaultHeight * 0.85],
        9:  [defaultWidth * 0.80, defaultHeight * 0.80],
        8:  [defaultWidth * 0.50, defaultHeight * 0.50],
        7:  [defaultWidth * 0.45, defaultHeight * 0.45],
        6:  [defaultWidth * 0.40, defaultHeight * 0.40],
        5:  [defaultWidth * 0.35, defaultHeight * 0.35],
        4:  [defaultWidth * 0.30, defaultHeight * 0.30],
        3:  [defaultWidth * 0.25, defaultHeight * 0.25]
      };
      
      switch(direction)
      {
        case "left":
            tooltipAnchor = [-15, 0];
            break;
        case "right":
            tooltipAnchor = [15, 0];
            break;
        case "top":
            tooltipAnchor = [0, -15];
            break;
        case "bottom":
            tooltipAnchor = [0, 15];
            break;
      }

      return L.icon({ iconUrl: "images/HD-Icon-Stack.png", iconSize: sizes[zoomLevel], tooltipAnchor: tooltipAnchor });
    });

    public static CreateContainerspotIcon = memoizeOne((zoomLevel) : L.Icon =>
    {        
      let defaultWidth = 34;
      let defaultHeight = 30;

      let sizes = {
        13: [defaultWidth, defaultHeight], //nearest zoom level, e.g. biggest icon size
        12: [defaultWidth * 0.95, defaultHeight * 0.95],
        11: [defaultWidth * 0.90, defaultHeight * 0.90],
        10: [defaultWidth * 0.85, defaultHeight * 0.85],
        9:  [defaultWidth * 0.80, defaultHeight * 0.80],
        8:  [defaultWidth * 0.50, defaultHeight * 0.50],
        7:  [defaultWidth * 0.45, defaultHeight * 0.45],
        6:  [defaultWidth * 0.40, defaultHeight * 0.40],
        5:  [defaultWidth * 0.35, defaultHeight * 0.35],
        4:  [defaultWidth * 0.30, defaultHeight * 0.30],
        3:  [defaultWidth * 0.25, defaultHeight * 0.25]
      };

      return L.icon({ iconUrl: "images/HD-Icon-Containerspot.png", iconSize: sizes[zoomLevel] });
    });

    public static CRS_OpenStreetMap = L.CRS.EPSG3857;

    public static CRS = new (L as any).Proj.CRS('EPSG:25832',
	      '+proj=utm +zone=32 +ellps=GRS80 +units=m +no_defs', {
        resolutions: [1638.4,819.2,409.6,204.8,102.4,51.2,25.6,12.8,6.4,3.2,1.6,0.8,0.4,0.2],
        origin: [120000,6500000],
        bounds: L.bounds([120000, 5661139.2],[1378291.2, 6500000])
    });
    
    public static ConfigureLeafLet()
    {
        (L as any).drawLocal.draw.handlers.polyline.tooltip.start = Utilities.getTranslation('leafletDraw.polyline.start', 'Klik på kortet for at starte linjen');
        (L as any).drawLocal.draw.handlers.polyline.tooltip.cont = Utilities.getTranslation('leafletDraw.polyline.cont', 'Klik på kortet for at fortsætte linjen');
        (L as any).drawLocal.draw.handlers.polyline.tooltip.end = Utilities.getTranslation('leafletDraw.polyline.end', 'Klik på det sidste punkt for at afslutte linjen');
        (L as any).drawLocal.draw.handlers.marker.tooltip.start = Utilities.getTranslation('leafletDraw.marker.start', 'Klik på kortet for at placere det valgte element');
        (L as any).drawLocal.draw.handlers.polygon.tooltip.start = Utilities.getTranslation('leafletDraw.polygon.start', 'Klik på kortet for at starte arbejdsområdet');
        (L as any).drawLocal.draw.handlers.polygon.tooltip.cont = Utilities.getTranslation('leafletDraw.polygon.cont', 'Klik på kortet for at tegne næste punkt i arbejdsområdet');
        (L as any).drawLocal.draw.handlers.polygon.tooltip.end = Utilities.getTranslation('leafletDraw.polygon.end', 'Klik på det første punkt for at afslutte arbejdsområdet');
        L.Icon.Default.imagePath='images/';
    }
}

export enum Status
{
    Open = "Kladde",
    WaitingForApproval = "Afventer Dalgas godkendelse",
    Rejected = "Afvist",
    RequiresAdditionalInfo = "Kræver yderligere info",
    Approved = "Godkendt",
    Completed = "Afsluttet",
    Offline = "Offline"
}

export class BackgroundMaps
{
    static Luftfoto = { type:"wmts",name: "Luftfoto", attribution: "Orto", url: "https://services.datafordeler.dk/GeoDanmarkOrto/orto_foraar_wmts/1.0.0/wmts?username=BUDFIWOUSX&password=2nq8kyU7Ne5gHv!!A&layer=orto_foraar_wmts&style=default&tilematrixset=KortforsyningTilingDK&Service=WMTS&Request=GetTile&Version=1.0.0&Format=image/jpeg&TileMatrix={z}&TileCol={x}&TileRow={y}", maxZoom: 13} as ILayer;
    static TopoDTK25 = { type:"wms", name: "Topo DTK25", attribution: "Topo DTK25", transparent: "TRUE", layers:"dtk25_2021", version: "1.3.0", url: "https://api.dataforsyningen.dk/dtk_25_DAF?token=ea2729ee97000593afc48e6feb11dfaf", maxZoom: 13} as ILayer;    
    static Skærmkort = { type:"wms", name: "Skærmkort", version: "1.1.1",transparent: "FALSE", layers: "dtk_skaermkort", url: "https://services.datafordeler.dk/Dkskaermkort/topo_skaermkort/1.0.0/WMS?username=BUDFIWOUSX&password=2nq8kyU7Ne5gHv!!A", maxZoom: 13 } as ILayer;                  
    static OpenStreetMap = { type:"wmts", name: "Open Street Map", url:"https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", maxZoom: 19 } as ILayer;
    
    // Germany WMS background maps
    static DTK25Germany = {
        type: "wms",
        name: "DTK25 Germany",
        attribution: "BKG Germany",
        transparent: "TRUE",
        layers: "DTK0025_BKG",
        version: "1.3.0",
        url: "https://gdw.navlog.de/data/navlog/wms?kid=hfg22tp.1",
        crs: MapUtilities.CRS,
        maxZoom: 13
    } as ILayer;
    static Luftbild = {
        type: "wms",
        name: "Luftbild",
        attribution: "BKG Germany",
        transparent: "TRUE",
        layers: "Luftbild_BKG",
        version: "1.3.0",
        url: "https://gdw.navlog.de/data/navlog/wms?kid=hfg22tp.1",
        crs: MapUtilities.CRS,
        maxZoom: 12
    } as ILayer;
}

export class MapLayers
{
    static BeskyttetNatur = { name: "Beskyttet natur §3", layers: "dai:bes_naturtyper,dai:bes_vandloebXXX", url: "https://arealeditering-dist-geo.miljoedddportal.dk/geoserver/wms", enabled: true, signature: "Signatur_beskyttetnatur.png"} as ILayer;
    static Fredninger = { name: "Fredninger", layers: "dai:fredede_omr", url: "https://arealeditering-dist-geo.miljoeportal.dk/geoserver/wms", enabled: true, signature: "Signatur_fredninger.png"} as ILayer;
    static Fortidsminder ={ name: "Fortidsminder", layers: "v_lokalitet_all", url: "https://www.kulturarv.dk/wms", enabled: true, signature: "Signatur_Fortidsminder.png", styles: "v_lokalitet_protected"} as ILayer;
    static Beskyttelseslinjer = { name: "Beskyttelseslinjer", layers: "v_reg_lokalitet_beskyttelse_areal", url: "https://www.kulturarv.dk/wms", enabled: true, signature: "Signatur_Beskyttelseslinjer.png"} as ILayer;
    static StenOgJorddiger = { name: "Beskyttede sten- og jorddiger", layers: "bes_sten_jorddiger_2022", url: "https://arealeditering-dist-geo.miljoeportal.dk/geoserver/wms", enabled: true, version: "1.3.0", signature: "Signatur_beskyttedestenjorddiger.png"} as ILayer;
    static Natura2000 = { name: "Natura 2000 områder", layers: "theme-pg-natura_2000_omraader", url: "https://miljoegis.mim.dk/wms?servicename=miljoegis-natura2000_wms", enabled: true, signature: "Signatur_natura2000omraader.png"} as ILayer;
    static HNV = { name: "HNV", layers: "theme-plangroen_hnvskov", url: "https://miljoegis.mim.dk/wms?servicename=miljoegis-plangroendk_wms", enabled: false, signature: "Signatur_hnvskov.png"} as ILayer;
    static Fredskovsarealer = { name: "Fredskovsarealer", layers: "FredskovsLinje_Gaeldende", url: "https://services.datafordeler.dk/MATRIKLEN2/MatGaeldendeOgForeloebigWMS/1.0.0/WMS?username=BUDFIWOUSX&password=2nq8kyU7Ne5gHv!!A", enabled: true, signature: "Signatur_fredskov.png"} as ILayer;
    static Matrikel = { name: "Matrikel", layers:"MatrikelSkel_Gaeldende,Centroide_Gaeldende", url: "https://services.datafordeler.dk/MATRIKLEN2/MatGaeldendeOgForeloebigWMS/1.0.0/WMS?username=BUDFIWOUSX&password=2nq8kyU7Ne5gHv!!A", enabled: false} as ILayer;
    static Vejnavne = { name: "Vejnavne", layers:"Vejnavne_ortofoto", url: "https://api.dataforsyningen.dk/forvaltning2?token=ea2729ee97000593afc48e6feb11dfaf", enabled: false} as ILayer;
    // German overlay layers
    static FaunaFloraHabitatDE = {
        name: "Fauna Flora Habitat",
        layers: "bfn_sch_Schutzbebiet:Fauna_Flora_Habitat_Gebiete",
        url: "https://geodienste.bfn.de/ogc/wfs/schutzgebiet",
        crs: MapUtilities.CRS,
        type: "wfs",
        enabled: true
    } as ILayer;
    static VogelschutzgebieteDE = {
        name: "Vogelschutzgebiete",
        layers: "bfn_sch_Schutzbebiet:Vogelschutzgebiete",
        url: "https://geodienste.bfn.de/ogc/wfs/schutzgebiet",
        crs: MapUtilities.CRS,
        type: "wfs",
        enabled: true
    } as ILayer;
    static Wendemoeglichkeiten = {
        type: "wms",
        name: "Wendemoeglichkeiten",
        attribution: "Wendemoeglichkeiten",
        transparent: "true",
        layers: "Wendemoeglichkeiten",
        version: "1.3.0",
        url: "https://gdw.navlog.de/data/navlog/wms?kid=hfg22tp.1",
        legendUrl: "https://gdw.navlog.de/data/navlog/wms?kid=hfg22tp.1&service=WMS&version=1.3.0&request=GetLegendGraphic&format=image%2Fpng&width=305&height=160&layer=Wendemoeglichkeiten",
        crs: MapUtilities.CRS,
        maxZoom: 13
    } as ILayer;
    static Forstwege = {
        type: "wms",
        name: "Forstwege",
        attribution: "Forstwege",
        transparent: "true",
        layers: "Forstwege",
        version: "1.3.0",
        url: "https://gdw.navlog.de/data/navlog/wms?kid=hfg22tp.1",
        legendUrl: "https://gdw.navlog.de/data/navlog/wms?kid=hfg22tp.1&service=WMS&version=1.3.0&request=GetLegendGraphic&format=image%2Fpng&width=305&height=160&layer=Forstwege",
        crs: MapUtilities.CRS,
        maxZoom: 13
    } as ILayer;
    static Rettungspunkte = {
        type: "wms",
        name: "Rettungspunkte",
        attribution: "Rettungspunkte",
        transparent: "true",
        layers: "navlog_rettungspunkte",
        version: "1.3.0",
        url: "https://gdw.navlog.de/data/navlog/wms?kid=hfg22tp.1",
        legendUrl: "https://gdw.navlog.de/data/navlog/wms?kid=hfg22tp.1&service=WMS&version=1.3.0&request=GetLegendGraphic&format=image%2Fpng&width=20&height=20&layer=navlog_rettungspunkte",
        crs: MapUtilities.CRS,
        maxZoom: 13
    } as ILayer;
}


export class OSM_MapLayers
{
    static StenOgJorddiger = { name: "Beskyttede sten- og jorddiger", layers: "bes_sten_jorddiger_2022", url: "https://areeleditering-dist-geo.miljoeportal.dk/geoserver/ows", enabled: true, signature: "Signatur_beskyttedestenjorddiger.png", dpi: 192, map_resolution: 192, format_options: "dpi:192", crs: L.CRS.EPSG4326} as ILayer;
}